import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";
import useAuth from "../hooks/useAuth";
import { Document, Page, pdfjs } from "react-pdf";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Drawer,
  Divider,
  Tooltip,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

// ✅ AYUDA
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

// ✅ FOTOS
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/** =========================
 *  Gestos / constantes
 * ========================= */
const TAP_MOVE_TOLERANCE_PX = 8;
const TAP_MAX_MS = 260;

const LONGPRESS_EDIT_MS = 1000;

const MIN_W = 40;
const MIN_H = 20;

const LAYOUT_KEY = "_layout_v1";
const FIRMAS_KEY = "_firmas_v1";

const PDF_ZOOM = 1.8;

/** =========================
 *  Layout fijo (manual)
 *  ✅ Manual SIEMPRE tendrá 2 firmas fijas
 *  Ajusta X/Y/W/H si tu PDF manual difiere.
 * ========================= */
const FIXED_LAYOUT = [
  {
    kind: "firmawm",
    x: 1298,
    y: 2179,
    w: 255,
    h: 104,
    type: "signature",
    tipo: "responsable_wemotion",
    locked: true,
  },
  {
    kind: "firmacliente",
    x: 387,
    y: 2183,
    w: 381,
    h: 98,
    type: "signature",
    tipo: "responsable_cliente",
    locked: true,
  },
];

const FIXED_KINDS = FIXED_LAYOUT.map((f) => f.kind);

const uid = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }
};

function normalizeField(f) {
  return {
    uid: String(f.uid || uid()),
    kind: String(f.kind || "texto"),
    type: f.type || (String(f.kind || "").startsWith("firma") ? "signature" : "text"),
    tipo: f.tipo,
    x: Number(f.x || 0),
    y: Number(f.y || 0),
    w: Math.max(MIN_W, Number(f.w || 160)),
    h: Math.max(MIN_H, Number(f.h || 32)),
    value: String(f.value || ""),
    locked: Boolean(f.locked),
  };
}

function ensureFirmaSlots(firmasObj) {
  const out = { ...(firmasObj || {}) };
  for (const f of FIXED_LAYOUT) {
    if (f.type === "signature" && f.tipo) {
      if (!(f.tipo in out)) out[f.tipo] = null;
    }
  }
  return out;
}

function upsertFixedFields(existingFields, valuesByKind) {
  const byKind = new Map(existingFields.map((f) => [f.kind, f]));
  const out = [...existingFields.filter((f) => !FIXED_KINDS.includes(f.kind))];

  for (const fixed of FIXED_LAYOUT) {
    const prev = byKind.get(fixed.kind);

    const next = normalizeField({
      uid: prev?.uid || uid(),
      kind: fixed.kind,
      type: fixed.type,
      tipo: fixed.tipo,
      x: fixed.x,
      y: fixed.y,
      w: fixed.w,
      h: fixed.h,
      locked: true,
      value: fixed.type === "text" ? (valuesByKind?.[fixed.kind] ?? prev?.value ?? "") : "",
    });

    out.push(next);
  }

  out.sort((a, b) => {
    const ia = FIXED_KINDS.includes(a.kind) ? FIXED_KINDS.indexOf(a.kind) : 999;
    const ib = FIXED_KINDS.includes(b.kind) ? FIXED_KINDS.indexOf(b.kind) : 999;
    return ia - ib;
  });

  return out;
}

/** =========================
 *  Dialog de firma
 * ========================= */
function SignatureDialog({ open, title, onClose, onSave }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
  };

  const getPos = (e) => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const r = c.getBoundingClientRect();
    const scaleX = c.width / r.width;
    const scaleY = c.height / r.height;
    return { x: (e.clientX - r.left) * scaleX, y: (e.clientY - r.top) * scaleY };
  };

  const onDown = (e) => {
    drawing.current = true;
    last.current = getPos(e);
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const p = getPos(e);

    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";

    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();

    last.current = p;
  };

  const onUp = () => {
    drawing.current = false;
  };

  const save = () => {
    const c = canvasRef.current;
    if (!c) return;
    onSave?.(c.toDataURL("image/png"));
  };

  useEffect(() => {
    if (!open) return;
    setTimeout(() => clear(), 0);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="sig-dialog-title">
      <DialogTitle id="sig-dialog-title">{title || "Firmar"}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            border: "1px solid rgba(0,0,0,0.2)",
            borderRadius: 1,
            overflow: "hidden",
            touchAction: "none",
            bgcolor: "white",
          }}
        >
          <canvas
            ref={canvasRef}
            width={900}
            height={300}
            style={{ width: "100%", height: 240, display: "block" }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onPointerLeave={onUp}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={clear}>Limpiar</Button>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>
          Guardar firma
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** =========================
 *  Dialog de ayuda (gestos)
 * ========================= */
function HelpDialog({ open, onClose, onDontShowAgain }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="help-dialog-title"
      aria-describedby="help-dialog-desc"
    >
      <DialogTitle id="help-dialog-title">Ayuda · Edición del parte</DialogTitle>

      <DialogContent dividers id="help-dialog-desc">
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Añadir campos
          </Typography>
          <Typography variant="body2">
            • <strong>Toque/click rápido</strong> en una zona vacía del PDF: crea un campo de texto.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Mover y redimensionar
          </Typography>
          <Typography variant="body2">• <strong>Arrastrar</strong> un campo: lo mueve.</Typography>
          <Typography variant="body2">
            • <strong>Pellizcar con 2 dedos</strong> sobre un campo: lo redimensiona.
          </Typography>
          <Typography variant="body2">
            • En PC: <strong>Shift + arrastrar</strong> sobre un campo: lo redimensiona.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Editar y borrar
          </Typography>
          <Typography variant="body2">
            • <strong>Mantener pulsado 1s</strong> sobre un campo de texto: editar.
          </Typography>
          <Typography variant="body2">
            • Para borrar un campo: abre el editor (mantener 1s) y pulsa <strong>Eliminar campo</strong>.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Firmas y fotos
          </Typography>
          <Typography variant="body2">
            • <strong>Tap</strong> en un recuadro de firma: se abre el panel de firma.
          </Typography>
          <Typography variant="body2">
            • Botón <strong>Fotos</strong>: abre el panel lateral para añadir evidencias.
          </Typography>

          <Alert severity="info">Consejo: si algo no se ve bien al imprimir, ajusta el campo y vuelve a guardar.</Alert>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onDontShowAgain}>No volver a mostrar</Button>
        <Button variant="contained" onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** =========================================================
 *  Página principal (MANUAL)
 * ======================================================= */
export const ParteTrabajoManualPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { auth } = useAuth();

  // readOnly desde navegación (listado)
  const readOnlyFromNav = Boolean(location?.state?.readOnly);

  /** =========================
   *  Estado base
   * ========================= */
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [parte, setParte] = useState(null);

  // dueño real del parte (para evitar bypass por URL)
  const isOwner = useMemo(() => {
    if (!auth?.id) return false;
    if (!parte?.creado_por) return false;
    return String(parte.creado_por) === String(auth.id);
  }, [auth?.id, parte?.creado_por]);

  // readOnly efectivo
  const readOnly = useMemo(() => {
    if (!auth?.id) return true;
    return readOnlyFromNav || !isOwner;
  }, [auth?.id, readOnlyFromNav, isOwner]);

  /** =========================
   *  ✅ AYUDA
   * ========================= */
  const HELP_KEY = "pt_manual_help_seen_v1";
  const [helpOpen, setHelpOpen] = useState(false);

  const openHelp = useCallback(() => setHelpOpen(true), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);

  const markHelpSeen = useCallback(() => {
    try {
      localStorage.setItem(HELP_KEY, "1");
    } catch {}
  }, []);

  const dontShowHelpAgain = useCallback(() => {
    markHelpSeen();
    setHelpOpen(false);
  }, [markHelpSeen]);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(HELP_KEY) === "1";
      if (!seen) setHelpOpen(true);
    } catch {}
  }, []);

  /** =========================
   *  URL absoluta para /api/uploads
   * ========================= */
  const API_BASE = Global.url || "/api/";
  const SERVER_BASE = useMemo(() => {
    if (/^https?:\/\//i.test(API_BASE)) return API_BASE.replace(/\/api\/?$/, "");
    return "";
  }, [API_BASE]);

  const toAbs = useCallback(
    (p) => {
      if (!p || typeof p !== "string") return null;
      if (p.startsWith("data:image/")) return p;
      if (p.startsWith("http")) return p;
      if (p.startsWith("/")) return `${SERVER_BASE}${p}`;
      return p;
    },
    [SERVER_BASE]
  );

  /** =========================
   *  PDF sizing ✅ (scroll móvil como el de intervención)
   * ========================= */
  const viewportRef = useRef(null); // wrapper scrollable
  const containerRef = useRef(null); // stage (coords)
  const [pageSize, setPageSize] = useState({ width: 1, height: 1 });
  const [containerWidth, setContainerWidth] = useState(900);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      const cur = viewportRef.current;
      if (cur) setContainerWidth(cur.clientWidth || 900);
    });

    ro.observe(el);
    setContainerWidth(el.clientWidth || 900);

    return () => ro.disconnect();
  }, []);

  const renderWidth = useMemo(() => Math.max(1, Math.floor(containerWidth * PDF_ZOOM)), [containerWidth]);
  const scale = useMemo(() => renderWidth / (pageSize.width || 1), [renderWidth, pageSize.width]);

  /** =========================
   *  Auth headers
   * ========================= */
  const token = useMemo(() => localStorage.getItem("token"), []);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);

  /** =========================
   *  PDF memo
   * ========================= */
  const pdfUrl = "/Intervencion.pdf"; // cambia si manual usa otro PDF
  const file = useMemo(() => ({ url: pdfUrl }), []);
  const options = useMemo(() => ({ enableXfa: false }), []);

  /** =========================
   *  Cargar parte manual
   * ========================= */
  const cargarParteManual = useCallback(async () => {
    if (!id) {
      setError("id inválido");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const resGet = await fetch(`${Global.url}partes-trabajo/manual/${id}`, { headers });
      if (!resGet.ok) {
        const txt = await resGet.text().catch(() => "");
        throw new Error(txt || "Error al cargar el parte manual");
      }
      const data = await resGet.json();
      setParte(data?.data || data);
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar el parte manual");
    } finally {
      setLoading(false);
    }
  }, [id, headers]);

  useEffect(() => {
    cargarParteManual();
  }, [cargarParteManual]);

  /** =========================
   *  Layout (manual) ✅ inserta firmas fijas
   * ========================= */
  const [fields, setFields] = useState([]);

  useEffect(() => {
    if (!parte) return;

    const raw = parte?.datos?.[LAYOUT_KEY];
    const existing = Array.isArray(raw) ? raw.map(normalizeField) : [];

    const withFixed = upsertFixedFields(existing, null);

    const firmasActuales = parte?.datos?.[FIRMAS_KEY] || {};
    const firmasConSlots = ensureFirmaSlots(firmasActuales);

    setFields(withFixed);
    setParte((prev) => ({
      ...prev,
      datos: { ...(prev?.datos || {}), [LAYOUT_KEY]: withFixed, [FIRMAS_KEY]: firmasConSlots },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parte?.id]);

  useEffect(() => {
    if (!parte) return;
    setParte((prev) => ({ ...prev, datos: { ...(prev?.datos || {}), [LAYOUT_KEY]: fields } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  /** =========================
   *  Firmas (manual)
   * ========================= */
  const firmasLocal = useMemo(() => parte?.datos?.[FIRMAS_KEY] || {}, [parte?.datos]);

  const getFirmaTipo = (field) =>
    field?.tipo || (field?.kind === "firmacliente" ? "responsable_cliente" : "responsable_wemotion");

  const getFirmaSrc = (field) => toAbs(firmasLocal?.[getFirmaTipo(field)] || null);

  const [firmaField, setFirmaField] = useState(null);

  const guardarFirma = async (firmaDataUrl) => {
    if (readOnly) return toast.info("Este parte está en solo lectura.");
    if (!parte?.id || !firmaField) return;

    const tipo = getFirmaTipo(firmaField);

    try {
      const res = await fetch(`${Global.url}partes-trabajo/manual/${parte.id}/firmas`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          tipo,
          firma_data_url: firmaDataUrl,
          nombre_firmante: null,
          cargo_firmante: null,
          email_firmante: null,
        }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Error guardando firma");
      }

      const out = await res.json().catch(() => ({}));
      const ruta = out?.data?.ruta_firma || null;

      const nuevosFirmas = { ...(parte?.datos?.[FIRMAS_KEY] || {}), [tipo]: ruta || firmaDataUrl };
      const datosActualizados = { ...(parte?.datos || {}), [FIRMAS_KEY]: nuevosFirmas };

      setParte((prev) => ({ ...prev, datos: datosActualizados }));

      await fetch(`${Global.url}partes-trabajo/manual/${parte.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ datos: datosActualizados }),
      }).catch(() => {});

      toast.success("Firma guardada");
      setFirmaField(null);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo guardar la firma");
    }
  };

  /** =========================
   *  Fotos
   * ========================= */
  const [fotosOpen, setFotosOpen] = useState(false);
  const [fotosLoading, setFotosLoading] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotos, setFotos] = useState([]);

  const cargarFotos = useCallback(async () => {
    if (!parte?.id) return;
    setFotosLoading(true);
    try {
      const res = await fetch(`${Global.url}partes-trabajo/manual/${parte.id}/fotos`, { headers });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Error al cargar fotos");
      }
      const out = await res.json().catch(() => ({}));
      setFotos(Array.isArray(out?.data) ? out.data : []);
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron cargar las fotos");
    } finally {
      setFotosLoading(false);
    }
  }, [parte?.id, headers, toast]);

  useEffect(() => {
    if (fotosOpen) cargarFotos();
  }, [fotosOpen, cargarFotos]);

  const subirFoto = async (fileObj) => {
    if (readOnly) return toast.info("Este parte está en solo lectura.");
    if (!parte?.id || !fileObj) return;

    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);

      const res = await fetch(`${Global.url}partes-trabajo/manual/${parte.id}/fotos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Error subiendo foto");
      }

      const out = await res.json().catch(() => ({}));
      const nueva = out?.data;
      if (nueva) setFotos((prev) => [...prev, nueva]);
      toast.success("Foto añadida");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo subir la foto");
    } finally {
      setUploadingFoto(false);
    }
  };

  const borrarFoto = async (fotoId) => {
    if (readOnly) return toast.info("Este parte está en solo lectura.");
    if (!fotoId) return;

    try {
      const res = await fetch(`${Global.url}partes-trabajo/manual/fotos/${fotoId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Error borrando foto");
      }
      setFotos((prev) => prev.filter((f) => f.id !== fotoId));
      toast.success("Foto eliminada");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo eliminar la foto");
    }
  };

  /** =========================
   *  Editor texto
   *  ✅ elimina desde el modal (no gesto 5s)
   * ========================= */
  const [editFieldId, setEditFieldId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const editField = useMemo(
    () => (editFieldId ? fields.find((f) => f.uid === editFieldId) : null),
    [editFieldId, fields]
  );

  const openEditorFor = useCallback(
    (fieldId) => {
      if (readOnly) return;
      const f = fields.find((x) => x.uid === fieldId);
      if (!f || f.locked || f.type === "signature") return;
      setEditFieldId(fieldId);
      setEditValue(f.value || "");
    },
    [fields, readOnly]
  );

  const closeEditor = () => {
    setEditFieldId(null);
    setEditValue("");
  };

  const deleteField = useCallback(
    (fieldId) => {
      if (readOnly) return;
      const f = fields.find((x) => x.uid === fieldId);
      if (!f || f.locked || f.type === "signature") return;
      setFields((prev) => prev.filter((x) => x.uid !== fieldId));
    },
    [fields, readOnly]
  );

  const saveEditor = () => {
    if (readOnly) return;
    if (!editFieldId) return;
    setFields((prev) => prev.map((f) => (f.uid === editFieldId ? { ...f, value: editValue } : f)));
    closeEditor();
  };

  /** =========================
   *  Gestos ✅ (sin borrado 5s)
   * ========================= */
  const containerPointerDownRef = useRef(new Map());
  const activeFieldPointersRef = useRef(new Map());
  const gestureRef = useRef({ fieldId: null, mode: null, pinch: null });

  const longPressRef = useRef({
    fieldId: null,
    editReady: false,
    editTimer: null,
    startClient: null,
    moved: false,
  });

  const clearLongPress = () => {
    if (longPressRef.current.editTimer) clearTimeout(longPressRef.current.editTimer);
    longPressRef.current = {
      fieldId: null,
      editReady: false,
      editTimer: null,
      startClient: null,
      moved: false,
    };
  };

  const pdfPointFromClient = (clientX, clientY) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const b = el.getBoundingClientRect();
    return { x: (clientX - b.left) / scale, y: (clientY - b.top) / scale };
  };

  const onContainerPointerDown = (e) => {
    if (readOnly) return;
    if (e.target?.dataset?.field === "1") return;
    containerPointerDownRef.current.set(e.pointerId, {
      startClient: { x: e.clientX, y: e.clientY },
      startAt: Date.now(),
    });
  };

  const onContainerPointerUp = (e) => {
    if (readOnly) return;

    const down = containerPointerDownRef.current.get(e.pointerId);
    containerPointerDownRef.current.delete(e.pointerId);
    if (!down) return;

    const dx = Math.abs(e.clientX - down.startClient.x);
    const dy = Math.abs(e.clientY - down.startClient.y);
    const dt = Date.now() - down.startAt;

    if (dx <= TAP_MOVE_TOLERANCE_PX && dy <= TAP_MOVE_TOLERANCE_PX && dt <= TAP_MAX_MS) {
      const p = pdfPointFromClient(e.clientX, e.clientY);
      const nf = normalizeField({
        uid: uid(),
        kind: "texto",
        type: "text",
        x: Math.max(0, p.x),
        y: Math.max(0, p.y),
        w: 220,
        h: 34,
        value: "",
        locked: false,
      });
      setFields((prev) => [...prev, nf]);
    }
  };

  const onFieldPointerDown = (fieldId, e) => {
    e.stopPropagation();
    // ✅ iOS/Android: evita que el pan/scroll se coma el drag del field
    e.preventDefault();

    const f = fields.find((x) => x.uid === fieldId);
    if (!f) return;

    if (readOnly) return;

    if (f.type === "signature") {
      setFirmaField(f);
      return;
    }
    if (f.locked) return;

    const pt = pdfPointFromClient(e.clientX, e.clientY);

    activeFieldPointersRef.current.set(e.pointerId, {
      fieldId,
      pointerType: e.pointerType,
      startClient: { x: e.clientX, y: e.clientY },
      startPdf: pt,
      startRectSnapshot: { x: f.x, y: f.y, w: f.w, h: f.h },
      currentClient: { x: e.clientX, y: e.clientY },
    });

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}

    clearLongPress();
    longPressRef.current.fieldId = fieldId;
    longPressRef.current.startClient = { x: e.clientX, y: e.clientY };
    longPressRef.current.moved = false;

    longPressRef.current.editTimer = setTimeout(() => {
      if (gestureRef.current.mode === "pinch") return;
      if (longPressRef.current.moved) return;
      longPressRef.current.editReady = true;
      openEditorFor(fieldId);
    }, LONGPRESS_EDIT_MS);
  };

  const onPointerMove = (e) => {
    if (readOnly) return;

    const ap = activeFieldPointersRef.current.get(e.pointerId);
    if (!ap) return;

    ap.currentClient = { x: e.clientX, y: e.clientY };

    if (longPressRef.current.startClient) {
      const mdx = Math.abs(e.clientX - longPressRef.current.startClient.x);
      const mdy = Math.abs(e.clientY - longPressRef.current.startClient.y);
      if (mdx > TAP_MOVE_TOLERANCE_PX || mdy > TAP_MOVE_TOLERANCE_PX) longPressRef.current.moved = true;
    }

    const fieldId = ap.fieldId;

    const same = [];
    for (const [, v] of activeFieldPointersRef.current.entries()) if (v.fieldId === fieldId) same.push(v);

    if (same.length >= 2) {
      clearLongPress();
      const [p1, p2] = same;

      if (gestureRef.current.fieldId !== fieldId || gestureRef.current.mode !== "pinch" || !gestureRef.current.pinch) {
        const dx0 = Math.abs(p2.startPdf.x - p1.startPdf.x) || 1;
        const dy0 = Math.abs(p2.startPdf.y - p1.startPdf.y) || 1;

        const r0 = p1.startRectSnapshot;
        const cx0 = r0.x + r0.w / 2;
        const cy0 = r0.y + r0.h / 2;

        gestureRef.current = {
          fieldId,
          mode: "pinch",
          pinch: { dx0, dy0, rect0: { ...r0 }, c0: { x: cx0, y: cy0 } },
        };
      }

      const pinch = gestureRef.current.pinch;
      if (!pinch) return;

      const p1now = pdfPointFromClient(p1.currentClient.x, p1.currentClient.y);
      const p2now = pdfPointFromClient(p2.currentClient.x, p2.currentClient.y);

      const dx1 = Math.abs(p2now.x - p1now.x) || 1;
      const dy1 = Math.abs(p2now.y - p1now.y) || 1;

      const sx = dx1 / pinch.dx0;
      const sy = dy1 / pinch.dy0;

      const nw = Math.max(MIN_W, pinch.rect0.w * sx);
      const nh = Math.max(MIN_H, pinch.rect0.h * sy);

      const nx = Math.max(0, pinch.c0.x - nw / 2);
      const ny = Math.max(0, pinch.c0.y - nh / 2);

      setFields((prev) => prev.map((f) => (f.uid === fieldId ? { ...f, x: nx, y: ny, w: nw, h: nh } : f)));
      return;
    }

    clearLongPress();

    const dxPdf = (e.clientX - ap.startClient.x) / scale;
    const dyPdf = (e.clientY - ap.startClient.y) / scale;

    if (ap.pointerType === "mouse" && e.shiftKey) {
      const nw = Math.max(MIN_W, ap.startRectSnapshot.w + dxPdf);
      const nh = Math.max(MIN_H, ap.startRectSnapshot.h + dyPdf);
      setFields((prev) => prev.map((f) => (f.uid === fieldId ? { ...f, w: nw, h: nh } : f)));
      return;
    }

    setFields((prev) =>
      prev.map((f) =>
        f.uid === fieldId
          ? { ...f, x: Math.max(0, ap.startRectSnapshot.x + dxPdf), y: Math.max(0, ap.startRectSnapshot.y + dyPdf) }
          : f
      )
    );
  };

  const onPointerUpOrCancel = (e) => {
    containerPointerDownRef.current.delete(e.pointerId);
    activeFieldPointersRef.current.delete(e.pointerId);

    let still = false;
    for (const [, v] of activeFieldPointersRef.current.entries()) {
      if (v.fieldId === gestureRef.current.fieldId) {
        still = true;
        break;
      }
    }
    if (!still) gestureRef.current = { fieldId: null, mode: null, pinch: null };

    clearLongPress();
  };

  /** =========================
   *  Guardar parte manual
   * ========================= */
  const onGuardar = async () => {
    if (readOnly) return toast.info("Este parte está en solo lectura.");
    if (!parte?.id) return;

    setGuardando(true);
    try {
      const res = await fetch(`${Global.url}partes-trabajo/manual/${parte.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ datos: parte.datos }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Error al guardar");
      }

      const out = await res.json();
      const updated = out?.data || out;
      setParte(updated);
      toast.success("Guardado");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  /** =========================
   *  Render: estados
   * ========================= */
  if (loading) {
    return (
      <Box className="appContainer appContainer--wide">
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={22} />
            <Typography>Cargando…</Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="appContainer appContainer--wide">
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                Volver
              </Button>
              <Button variant="contained" onClick={cargarParteManual}>
                Reintentar
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /** =========================
   *  Render: normal
   * ========================= */
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <IconButton onClick={() => navigate(-1)} size="small" aria-label="Volver">
                <ArrowBackIcon />
              </IconButton>

              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Parte manual
              </Typography>

              {readOnly ? (
                <Chip size="small" label="Solo lectura" variant="outlined" />
              ) : (
                <Chip size="small" label="Editable" />
              )}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" startIcon={<PhotoCameraIcon />} onClick={() => setFotosOpen(true)} disabled={!parte?.id}>
                Fotos
              </Button>

              <Tooltip title={readOnly ? "Solo el creador puede guardar cambios" : ""}>
                <span>
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={onGuardar} disabled={guardando || readOnly}>
                    {guardando ? "Guardando…" : "Guardar"}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          {/* ✅ scroll móvil + zoom, como en intervención */}
          <Box
            ref={viewportRef}
            sx={{
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              maxHeight: { xs: "calc(100dvh - 220px)", md: "none" },
              borderRadius: 1,
            }}
          >
            <Box
              ref={containerRef}
              sx={{
                position: "relative",
                width: renderWidth,
                touchAction: "pan-x pan-y",
              }}
              onPointerDown={onContainerPointerDown}
              onPointerUp={onContainerPointerUp}
              onPointerMove={onPointerMove}
              onPointerCancel={onPointerUpOrCancel}
            >
              <Document
                file={file}
                options={options}
                loading={
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Cargando PDF…</Typography>
                  </Stack>
                }
                error={
                  <Alert severity="error" sx={{ m: 2 }}>
                    No se pudo cargar el PDF.
                  </Alert>
                }
              >
                <Page
                  pageNumber={1}
                  width={renderWidth}
                  onLoadSuccess={(p) => setPageSize({ width: p.width, height: p.height })}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {fields.map((f) => {
                const left = f.x * scale;
                const top = f.y * scale;
                const width = f.w * scale;
                const height = f.h * scale;

                const fontPdf = f.locked ? 9.5 : 10.8;
                const fontPx = Math.max(13, Math.round(fontPdf * scale));
                const fontFamily = "Arial Narrow, Helvetica Neue Condensed, Roboto Condensed, Arial, sans-serif";
                const fontWeight = f.locked ? 650 : 750;

                const isSignature = f.type === "signature";
                const firmaSrc = isSignature ? getFirmaSrc(f) : null;

                return (
                  <Box
                    key={f.uid}
                    data-field="1"
                    onPointerDown={(e) => onFieldPointerDown(f.uid, e)}
                    onPointerUp={onPointerUpOrCancel}
                    onPointerCancel={onPointerUpOrCancel}
                    sx={{
                      position: "absolute",
                      left,
                      top,
                      width,
                      height,
                      border: "1px solid rgba(0,0,0,0.18)",
                      bgcolor: "rgba(255,255,255,0.35)",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1,
                      overflow: "hidden",
                      userSelect: "none",
                      cursor: readOnly ? "default" : isSignature ? "pointer" : f.locked ? "default" : "move",
                      WebkitTapHighlightColor: "transparent",
                      touchAction: "none",
                    }}
                    role={isSignature ? "button" : undefined}
                    aria-label={isSignature ? `Firmar: ${f.kind}` : undefined}
                  >
                    {isSignature ? (
                      firmaSrc ? (
                        <Box
                          component="img"
                          src={firmaSrc}
                          alt={`Firma ${f.kind}`}
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 12, opacity: 0.6, fontWeight: 800 }}>
                          {readOnly ? "Firma no disponible" : "Tap para firmar"}
                        </Typography>
                      )
                    ) : (
                      <Typography
                        sx={{
                          width: "100%",
                          fontFamily,
                          fontWeight,
                          fontSize: `${fontPx}px`,
                          lineHeight: 1.1,
                          letterSpacing: "0.2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "rgba(0,0,0,0.88)",
                          opacity: f.value ? 1 : 0.6,
                        }}
                      >
                        {f.value || ""}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      </Stack>

      {/* ✅ Ayuda */}
      <HelpDialog open={helpOpen} onClose={closeHelp} onDontShowAgain={dontShowHelpAgain} />

      {/* Editor texto ✅ con botón eliminar */}
      <Dialog open={Boolean(editFieldId)} onClose={closeEditor} fullWidth maxWidth="sm" aria-labelledby="edit-dialog-title">
        <DialogTitle id="edit-dialog-title">Editar</DialogTitle>
        <DialogContent>
          {readOnly ? (
            <Alert severity="info">Este parte está en solo lectura.</Alert>
          ) : editField?.locked || editField?.type === "signature" ? (
            <Alert severity="info">Este campo no es editable.</Alert>
          ) : (
            <TextField value={editValue} onChange={(e) => setEditValue(e.target.value)} fullWidth multiline minRows={4} autoFocus />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            onClick={() => {
              if (!editFieldId) return;
              deleteField(editFieldId);
              closeEditor();
            }}
            disabled={readOnly || Boolean(editField?.locked || editField?.type === "signature")}
          >
            Eliminar campo
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button onClick={closeEditor}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={saveEditor}
            disabled={readOnly || Boolean(editField?.locked || editField?.type === "signature")}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Firma */}
      <SignatureDialog
        open={Boolean(firmaField) && !readOnly}
        title={firmaField?.tipo === "responsable_cliente" ? "Firma cliente" : "Firma técnico (WeMotion)"}
        onClose={() => setFirmaField(null)}
        onSave={guardarFirma}
      />

      {/* ✅ FOTOS Drawer */}
      <Drawer anchor="right" open={fotosOpen} onClose={() => setFotosOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Fotos
            </Typography>
            <IconButton onClick={() => setFotosOpen(false)} aria-label="Cerrar fotos">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1}>
            <Tooltip title={readOnly ? "Solo el creador puede añadir fotos" : ""}>
              <span>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                  disabled={uploadingFoto || !parte?.id || readOnly}
                >
                  {uploadingFoto ? "Subiendo…" : "Hacer foto"}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) subirFoto(f);
                    }}
                  />
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={readOnly ? "Solo el creador puede añadir fotos" : ""}>
              <span>
                <Button variant="outlined" component="label" disabled={uploadingFoto || !parte?.id || readOnly}>
                  Elegir de galería
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) subirFoto(f);
                    }}
                  />
                </Button>
              </span>
            </Tooltip>

            {fotosLoading ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Cargando fotos…</Typography>
              </Stack>
            ) : fotos.length === 0 ? (
              <Alert severity="info">Aún no hay fotos.</Alert>
            ) : (
              <Stack spacing={1}>
                {fotos.map((ph) => {
                  const src = toAbs(ph.ruta_foto);
                  return (
                    <Paper key={ph.id} sx={{ p: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          component="img"
                          src={src}
                          alt={ph.title || "Foto"}
                          sx={{
                            width: 88,
                            height: 88,
                            objectFit: "cover",
                            borderRadius: 1,
                            bgcolor: "rgba(0,0,0,0.06)",
                            flex: "0 0 auto",
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {ph.title || "Sin título"}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.75 }} noWrap>
                            {ph.caption || ""}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6 }}>
                            Orden: {ph.sort_order ?? "-"}
                          </Typography>
                        </Box>

                        <Tooltip title={readOnly ? "Solo el creador puede eliminar fotos" : "Eliminar foto"}>
                          <span>
                            <IconButton
                              onClick={() => borrarFoto(ph.id)}
                              size="small"
                              aria-label="Eliminar foto"
                              disabled={readOnly}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Box>
      </Drawer>

      {/* ✅ FAB Ayuda (discreto) */}
      <Tooltip title="Ayuda (gestos)" placement="left">
        <Box sx={{ position: "fixed", right: 18, bottom: 18, zIndex: 1400 }}>
          <Button
            variant="contained"
            onClick={() => {
              markHelpSeen();
              openHelp();
            }}
            startIcon={<HelpOutlineIcon />}
            aria-label="Abrir ayuda"
            sx={{
              borderRadius: 999,
              px: 2,
              py: 1.2,
              boxShadow: 4,
              textTransform: "none",
              fontWeight: 900,
              minWidth: 120,
            }}
          >
            Ayuda
          </Button>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default ParteTrabajoManualPage;